'use client';
// Единый провайдер НАЗВАНИЙ аспектов (русские лейблы из БД через API).
// Один запрос /api/assessment/aspect-names при загрузке ЛК.
// Фолбэк — локальный словарь (защита от затухания, если API недоступен).
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchWithToken } from '../api-core';

type NamesMap = Record<string, string>; // aspectKey → русское название

type Ctx = {
  names: NamesMap;
  loaded: boolean;
  /** Русское название аспекта: API-словарь → локальный фолбэк → ключ */
  label: (aspectKey: string) => string;
};

const NamesCtx = createContext<Ctx>({ names: {}, loaded: false, label: (k) => k });

// Локальный фолбэк — минимальный, только самые частые ключи.
// ПОЛНЫЙ словарь живёт в БД (test_facets) — API отдаёт его через /assessment/aspect-names.
const FALLBACK: NamesMap = {
  'bigfive.neuroticism': 'Нейротизм',
  'bigfive.extraversion': 'Экстраверсия',
  'bigfive.openness': 'Открытость опыту',
  'bigfive.agreeableness': 'Доброжелательность',
  'bigfive.conscientiousness': 'Добросовестность',
  'disc.dominance': 'Доминирование',
  'disc.influence': 'Влияние',
  'disc.steadiness': 'Стабильность',
  'disc.conscientiousness': 'Добросовестность',
  'eq.self_emotion': 'Понимание своих эмоций',
  'eq.others_emotion': 'Понимание эмоций других',
  'eq.use_emotion': 'Использование эмоций',
  'eq.regulation': 'Саморегуляция',
};

export function AspectNamesProvider({ children }: { children: ReactNode }) {
  const [names, setNames] = useState<NamesMap>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const json: any = await fetchWithToken('/api/assessment/aspect-names');
        if (active && json?.data) {
          // data: { bigfive: {key: name}, disc: {...} } → flatten
          const flat: NamesMap = {};
          for (const group of Object.values<any>(json.data)) {
            if (group && typeof group === 'object') Object.assign(flat, group);
          }
          setNames(flat);
          setLoaded(true);
        }
      } catch {
        setLoaded(true); // не блокируем — работает фолбэк
      }
    })();
    return () => { active = false; };
  }, []);

  const label: Ctx['label'] = (aspectKey) => {
    const key = aspectKey.replace('bigfive.facet.', 'bigfive.'); // нормализация старых ключей
    return names[key] ?? FALLBACK[key] ?? key;
  };

  return <NamesCtx.Provider value={{ names, loaded, label }}>{children}</NamesCtx.Provider>;
}

export function useAspectNames() {
  return useContext(NamesCtx);
}
